import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Reescrita sobre CVA + Radix Slot, pero `variant` sigue aceptando los mismos
 * cuatro valores de siempre — nada de `default`/`destructive`/`outline` de
 * shadcn — para no tener que tocar los ~40 sitios que ya llaman `<Button
 * variant="...">`. `size` y `asChild` son nuevos y opcionales.
 *
 * `size: 'default'` reusa el padding exacto de antes (`px-3 py-1.5 text-sm`,
 * sin alto fijo): copiar el `h-10 px-4 py-2` de shadcn aquí habría cambiado
 * de tamaño a todos los botones existentes como efecto secundario de este
 * refactor, no como un cambio de diseño deliberado.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded text-sm transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        secondary: 'bg-accent/60 shadow-sm hover:bg-accent',
        danger: 'bg-destructive/10 text-destructive shadow-sm hover:bg-destructive/15',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'px-3 py-1.5 text-sm',
        sm: 'h-8 rounded px-2.5 text-xs',
        lg: 'h-11 rounded-md px-6',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
    );
  },
);
Button.displayName = 'Button';
